import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import { QUESTION_TYPE_LABELS, optionHasSpecify, noPath } from "./data";
import { questionLabel, OptionTag } from "./QuestionMeta";
import {
  Backdrop, Modal, Header, Title, CloseBtn, Body, Loading,
} from "../SiteInvitationList/emailModalUI";
import { narrativeViewerStyles } from "./narrativeStyles";
import { withTableWrappers } from "./narrativeHtml";

// What changed between two versions of a questionnaire. Sibling of VersionContent.js:
// that one renders one version in full, this one renders the delta the API computed
// (GET /api/sfq/:id/versions/:versionId/diff, or .../versions/working/diff).
//
// The diff is computed server-side — see services/Feasibility/versionDiff.js for the
// matching rules. Questions are matched on questionKey, the only identifier that
// survives a version boundary.

const Empty = styled.div`
  padding: 32px 0;
  text-align: center;
  color: #adb5bd;
  font-size: 13px;
`;

const Summary = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 14px;
`;

const COUNT_COLORS = {
  added:    { bg: "#e6f4ea", fg: "#137333" },
  removed:  { bg: "#fce8e6", fg: "#c5221f" },
  modified: { bg: "#fef7e0", fg: "#976800" },
  moved:    { bg: "#e8f0fe", fg: "#1967d2" },
  renamed:  { bg: "#f3e8fd", fg: "#7627bb" },
};

const Chip = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 11px;
  font-size: 12px;
  font-weight: 600;
  background: ${(p) => COUNT_COLORS[p.$kind]?.bg || "#f1f3f5"};
  color: ${(p) => COUNT_COLORS[p.$kind]?.fg || "#6c757d"};
`;

const SubLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6c757d;
  margin: 18px 0 8px;
  &:first-child { margin-top: 0; }
`;

const Note = styled.div`
  font-size: 12px;
  color: #868e96;
  margin-bottom: 8px;
  line-height: 1.5;
`;

// The redline the API builds (services/Feasibility/narrativeDiff.js): the new overview
// with the deleted parts kept in place, every change carrying a class. It is drawn with
// the same narrativeContentStyles as the viewer, so tables, lists, headings and inline
// marks look like the document they describe — the change marks sit on top of that.
const ADD_BG = "#d7f0dd";
const ADD_FG = "#137333";
const DEL_BG = "#fbdcd9";
const DEL_FG = "#c5221f";
const FMT_BG = "#f6ecfd";
const FMT_FG = "#7627bb";

// 본문 자체는 에디터와 같은 폭·글자 크기로 그린다. 변경 표시는 그 위에만 얹는다.
const NarrativeBox = styled.div`
  border: 1px solid #e9ecef;
  border-radius: 6px;
  color: #212529;

  ${narrativeViewerStyles}

  /* 인라인 변경 */
  ins.d-add {
    background: ${ADD_BG};
    color: #0f5132;
    text-decoration: none;
    border-radius: 2px;
  }
  del.d-del {
    background: ${DEL_BG};
    color: #842029;
    text-decoration: line-through;
    border-radius: 2px;
  }
  /* 글자만 그대로이고 서식이 바뀐 자리. 바뀐 서식 그대로 보여 주고 밑줄로만 표시한다 */
  span.d-fmt {
    background: ${FMT_BG};
    border-bottom: 1px dashed ${FMT_FG};
  }

  /* 설명(title)이 붙어 있는 자리임을 커서로 알린다. 서식 변경은 글자에 자국이 남지
     않아, 커서가 바뀌지 않으면 그 자리에 변경이 없다는 뜻이기도 하다. */
  [data-fmt], [data-fmt-row] { cursor: help; }

  /* 통째로 추가·삭제된 블록. 표 안에서는 배경이 셀 인라인 색에 밀리므로
     안쪽 그림자로 한 번 더 표시한다. 인라인 표시(ins·del)는 위에서 이미 칠했다. */
  .d-add:not(ins):not(del) {
    background: ${ADD_BG};
    box-shadow: inset 3px 0 0 ${ADD_FG};
  }
  .d-del:not(ins):not(del) {
    background: ${DEL_BG};
    box-shadow: inset 3px 0 0 ${DEL_FG};
    text-decoration: line-through;
  }
  .d-fmt-block {
    box-shadow: inset 3px 0 0 ${FMT_FG};
  }
  tr.d-add > td, tr.d-add > th { background: ${ADD_BG}; }
  tr.d-del > td, tr.d-del > th { background: ${DEL_BG}; text-decoration: line-through; }

  /* 행 자체의 속성만 바뀐 경우(행 높이). border-collapse 표에서는 tr에 준 배경·그림자가
     그려지지 않으므로, 위의 추가·삭제 행과 마찬가지로 셀에 붙여야 보인다. */
  tr.d-fmt-block > td:first-child,
  tr.d-fmt-block > th:first-child { box-shadow: inset 3px 0 0 ${FMT_FG}; }

  img.d-add { outline: 2px solid ${ADD_FG}; }
  img.d-del { outline: 2px solid ${DEL_FG}; opacity: 0.6; }
`;

const Ins = styled.ins`
  background: ${ADD_BG};
  color: #0f5132;
  text-decoration: none;
  border-radius: 2px;
`;

const Del = styled.del`
  background: ${DEL_BG};
  color: #842029;
  text-decoration: line-through;
  border-radius: 2px;
`;

const Fmt = styled.span`
  background: ${FMT_BG};
  border-bottom: 1px dashed ${FMT_FG};
`;

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  font-size: 11px;
  color: #868e96;
  margin-bottom: 8px;

  ins, del, ${Fmt} { padding: 0 4px; }
`;

const Group = styled.div`
  margin-bottom: 14px;
`;

const GroupTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${(p) => COUNT_COLORS[p.$kind]?.fg || "#495057"};
  margin-bottom: 6px;
`;

const Item = styled.div`
  padding: 8px 10px;
  border-left: 3px solid ${(p) => COUNT_COLORS[p.$kind]?.fg || "#dee2e6"};
  background: ${(p) => COUNT_COLORS[p.$kind]?.bg || "#f8f9fa"};
  border-radius: 0 4px 4px 0;
  margin-bottom: 4px;
  font-size: 13px;
  color: #212529;
`;

const Meta = styled.div`
  font-size: 11px;
  color: #6c757d;
  margin-top: 2px;
`;

// 질문 줄 맨 앞에 붙는 섹션 제목. 질문 자체보다 한 단계 여린 색으로 두어
// 같은 줄에 있어도 어디까지가 제목이고 어디부터가 질문인지 구분된다.
const SectionPrefix = styled.span`
  color: #868e96;
`;

const FieldRow = styled.div`
  font-size: 12px;
  margin-top: 4px;
  padding-left: 10px;
  border-left: 2px solid #e9ecef;
  color: #495057;
  line-height: 1.6;
`;

const OldVal = styled.span`
  background: #fbdcd9;
  color: #842029;
  padding: 0 4px;
  border-radius: 2px;
  text-decoration: line-through;
`;

const NewVal = styled.span`
  background: #d7f0dd;
  color: #0f5132;
  padding: 0 4px;
  border-radius: 2px;
`;

// 선택지 변경 한 줄. 표시(+ − · ↕)를 왼쪽에 고정 폭으로 세워, 여러 줄이 나와도
// 무엇이 추가·삭제·수정인지 세로로 훑어 읽힌다.
const OptionLine = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 2px;
`;

const OptionMark = styled.span`
  flex: none;
  width: 10px;
  text-align: center;
  font-weight: 700;
  color: ${(p) => COUNT_COLORS[p.$kind]?.fg || "#6c757d"};
`;

const OptionName = styled.span`
  font-weight: 600;
`;

// 추가·삭제된 선택지의 라벨과 그 선택지에 딸린 꼬리표들. 꼬리표가 여러 개 붙어도
// 줄바꿈되며 간격을 유지하도록 한 줄 안에서 다시 flex로 감싼다.
const OptionSummaryRow = styled.span`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px;
`;

// Human labels for the compared fields. Must cover every entry of COMPARED_FIELDS in
// services/Feasibility/versionDiff.js; anything unmapped falls back to the raw name.
// 섹션 제목은 여기 없다 — 제목 변경은 질문마다가 아니라 Sections에서 한 번만 알린다.
const FIELD_LABELS = {
  sectionNo:    "섹션 번호",
  parentKey:    "상위 문항",
  questionText: "질문 내용",
  questionType: "질문 유형",
  hasSpecify:   "Specify 사용",
  specifyLabel: "Specify 라벨",
  specifyWhen:  "Specify 표시 조건",
  options:      "선택지",
  singleSelect: "단일 선택",
  isRequired:   "필수 응답",
};

// Formatting properties the narrative redline reports, as `data-fmt` on the changed
// mark and in narrative.fmt. Names come from narrativeDiff.js — marks first, then the
// block/table attributes.
const FORMAT_LABELS = {
  bold:          "굵게",
  italic:        "기울임",
  underline:     "밑줄",
  strike:        "취소선",
  code:          "코드",
  sup:           "위 첨자",
  sub:           "아래 첨자",
  color:         "글자 색",
  fontSize:      "글자 크기",
  highlight:     "형광펜",
  link:          "링크",
  align:         "정렬",
  indent:        "들여쓰기",
  blockType:     "문단 종류",
  listType:      "목록 종류",
  listStyle:     "목록 기호",
  listStart:     "목록 시작 번호",
  itemValue:     "항목 번호",
  cellType:      "머리글 셀",
  checked:       "체크 표시",
  background:    "셀 배경색",
  verticalAlign: "세로 정렬",
  borderTop:     "위쪽 테두리",
  borderRight:   "오른쪽 테두리",
  borderBottom:  "아래쪽 테두리",
  borderLeft:    "왼쪽 테두리",
  colspan:       "가로 병합",
  rowspan:       "세로 병합",
  colwidth:      "열 너비",
  rowHeight:     "행 높이",
  tableAlign:    "표 위치",
  width:         "표 너비",
  minWidth:      "표 최소 너비",
  alt:           "이미지 설명",
};

// Field values are heterogeneous — strings, bits, arrays of codes. Rendered compactly
// rather than dumping raw JSON at the reviewer. `options` never comes through here: it
// is an array of objects and gets its own per-option diff (OptionsDiff).
//
// parentKey holds a questionKey, which is an internal identifier — resolved to the same
// "섹션-번호. 질문 문구" the rest of these screens name a question by. "(없음)" for a
// null one reads correctly on its own: the question is 상위 문항, it has no parent.
function fmtValue(field, value, resolveQuestion) {
  if (value == null || value === "") return "(없음)";
  if (typeof value === "boolean") return value ? "예" : "아니오";
  if (field === "questionType") return QUESTION_TYPE_LABELS[value] || value;
  if (field === "parentKey") return resolveQuestion ? resolveQuestion(value) : value;
  if (Array.isArray(value)) return value.length ? value.join(", ") : "(없음)";
  return String(value);
}

// ── 선택지 ────────────────────────────────────────────────────────────────────
//
// The API compares the options array as a single unit (versionDiff.js COMPARED_FIELDS),
// so what arrives is the whole old list and the whole new list. Printing them side by
// side means the reviewer has to eyeball two near-identical comma-joined sentences to
// find the one option that changed. So the list is paired up here and only the options
// that actually differ are shown, one line each.

const optionName = (o) => o.label || o.key || "(제목 없음)";

// 짝짓기 기준은 key다. key는 응답에 저장되는 값이라 라벨을 고쳐도 그대로 남는다.
// key가 없는(구버전) 선택지는 라벨로, 그것도 없으면 위치로 짝짓는다.
const optionId = (o, i) => (o.key ? `k:${o.key}` : o.label ? `l:${o.label}` : `i:${i}`);

// 옵션을 선택하면 열리는 질문들. showsQuestions에는 questionKey가 들어 있지만 key는
// 내부 식별자이므로, API가 함께 보내 준 questionIndex로 "섹션-번호. 질문 문구"로 푼다
// (읽기 전용 화면들과 같은 형식 — QuestionMeta.questionLabel).
function makeResolveQuestion(index) {
  // questionIndex는 questionKey로 찾는 map이라 그대로 questionLabel의 byKey가 된다.
  // 하위 문항의 번호는 상위마다 1부터 다시 매겨지므로, 이 map으로 부모를 한 번 더
  // 찾아 "2-3-1."까지 만들어야 어느 질문인지 정해진다.
  const byKey = index || {};
  return (key) => {
    const q = byKey[key];
    if (!q) return "(없는 질문)";
    // 이전 버전에만 있던 질문. 분기는 남아 있는데 대상이 사라진 상태다.
    return q.removed ? `${questionLabel(q, byKey)} (삭제됨)` : questionLabel(q, byKey);
  };
}

const fmtShows = (o, resolve) => (o.showsQuestions?.length
  ? o.showsQuestions.map((k) => (resolve ? resolve(k) : k)).join(", ")
  : "(없음)");

// 한 선택지에서 비교하는 속성들. 값은 화면에 그대로 나가는 문자열로 만든다.
// 라벨은 "무엇이 일어나는가"로 적는다 — 이 줄의 →는 이전 값에서 새 값으로라는 뜻이라,
// 조건부 표시까지 화살표로 적으면 두 화살표의 뜻이 섞인다.
const OPTION_PROPS = [
  ["라벨", (o) => o.label || "(제목 없음)"],
  ["선택 시 입력란", (o) => (optionHasSpecify(o)
    ? (o.specifyLabel ? `열림 · "${o.specifyLabel}"` : "열림")
    : "없음")],
  ["안내 문구", (o) => o.helperText || "(없음)"],
  ["선택 시 표시되는 질문", fmtShows],
];

// { added, removed, modified, reordered } — 실제로 달라진 것만.
function diffOptions(oldArr, newArr, resolve) {
  const before = (Array.isArray(oldArr) ? oldArr : []).map((o, i) => ({ o, id: optionId(o, i) }));
  const after  = (Array.isArray(newArr) ? newArr : []).map((o, i) => ({ o, id: optionId(o, i) }));

  const used = new Set();
  const added = [], modified = [];
  const commonNew = [];

  after.forEach(({ o: newO, id }) => {
    const hit = before.find((b) => b.id === id && !used.has(b));
    if (!hit) { added.push(newO); return; }
    used.add(hit);
    commonNew.push(id);
    const props = OPTION_PROPS
      .map(([label, get]) => [label, get(hit.o, resolve), get(newO, resolve)])
      .filter(([, oldV, newV]) => oldV !== newV);
    if (props.length) modified.push({ name: optionName(newO), props });
  });

  const removed = before.filter((b) => !used.has(b)).map((b) => b.o);
  // 양쪽에 다 있는 선택지들만 놓고 본 순서. 추가·삭제로 밀린 것은 순서 변경이 아니다.
  const commonOld = before.filter((b) => used.has(b)).map((b) => b.id);
  const reordered = commonOld.join(" ") !== commonNew.join(" ");

  return { added, removed, modified, reordered };
}

// 통째로 추가·삭제된 선택지 한 줄. 짝이 없어 old → new 로 적을 것이 없으므로, 그 선택지가
// 무엇을 하는 선택지였는지를 그대로 펼친다. 꼬리표는 Questions 탭(QuestionMeta)과 같은
// 것을 써서, 리뷰어가 저쪽에서 보던 말 그대로 읽게 한다 — 특히 "선택 시 …"이라는 표현이
// 그 자리에서 무엇이 열리는 조건인지 말해 준다.
function OptionSummary({ option: o, resolve, $kind }) {
  const Val = $kind === "added" ? NewVal : OldVal;
  return (
    <OptionSummaryRow>
      <Val>{optionName(o)}</Val>
      {optionHasSpecify(o) && (
        <OptionTag title="이 선택지를 고르면 자유 입력란이 나타납니다.">
          선택 시 입력란{o.specifyLabel ? `: ${o.specifyLabel}` : ""}
        </OptionTag>
      )}
      {o.helperText && (
        <OptionTag title="선택지 아래에 항상 표시되는 안내문">안내: {o.helperText}</OptionTag>
      )}
      {!!o.showsQuestions?.length && (
        <OptionTag $shows title="이 선택지를 고르면 아래 질문이 나타납니다.">
          선택 시 표시: {fmtShows(o, resolve)}
        </OptionTag>
      )}
    </OptionSummaryRow>
  );
}

function OptionsDiff({ value, resolve }) {
  const { added, removed, modified, reordered } = diffOptions(value.old, value.new, resolve);

  // 짝지을 수 없는 변경(예: key도 라벨도 통째로 바뀐 경우)은 추가·삭제로 갈린다.
  // 그때도 무엇이 사라지고 무엇이 생겼는지는 그대로 읽힌다.
  return (
    <FieldRow>
      {FIELD_LABELS.options}
      {added.map((o, i) => (
        <OptionLine key={`a${i}`}>
          <OptionMark $kind="added">+</OptionMark>
          <OptionSummary option={o} resolve={resolve} $kind="added" />
        </OptionLine>
      ))}
      {removed.map((o, i) => (
        <OptionLine key={`r${i}`}>
          <OptionMark $kind="removed">−</OptionMark>
          <OptionSummary option={o} resolve={resolve} $kind="removed" />
        </OptionLine>
      ))}
      {modified.map((m, i) => (
        <OptionLine key={`m${i}`}>
          <OptionMark $kind="modified">·</OptionMark>
          <span>
            <OptionName>{m.name}</OptionName>
            {m.props.map(([label, oldV, newV]) => (
              <span key={label}>
                {" "}— {label} <OldVal>{oldV}</OldVal> → <NewVal>{newV}</NewVal>
              </span>
            ))}
          </span>
        </OptionLine>
      ))}
      {reordered && (
        <OptionLine>
          <OptionMark $kind="moved">↕</OptionMark>
          <span>순서 변경 — {(value.new || []).map(optionName).join(", ")}</span>
        </OptionLine>
      )}
    </FieldRow>
  );
}

// 섹션은 질문과 같은 줄에 둔다 — 어느 섹션의 몇 번 질문인지가 한 호흡으로 읽힌다.
// 아래 줄에는 questionKey만 남긴다. 하위 문항이면 번호 앞에 상위 문항의 번호가 붙어
// "3-1."이 된다 — 하위 번호는 상위마다 1부터 다시 매겨져 홀로는 질문을 가리키지 못한다.
function QuestionLabel({ q, byKey }) {
  return (
    <>
      {q.sectionTitle && <SectionPrefix>{q.sectionTitle} - </SectionPrefix>}
      <b>{noPath(q, byKey)}</b>. {q.questionText || "(내용 없음)"}{" "}
      <span style={{ color: "#868e96", fontSize: 12 }}>
        ({QUESTION_TYPE_LABELS[q.questionType] || q.questionType})
      </span>
      <Meta>{q.questionKey}</Meta>
    </>
  );
}

function NarrativeDiff({ narrative }) {
  const boxRef = useRef(null);

  // The redline is one HTML string, so the format labels can't be rendered as props.
  // The API names the properties that changed on each mark (`data-fmt="bold color"`);
  // they become a tooltip once the string is in the DOM.
  useEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    box.querySelectorAll("[data-fmt]").forEach((el) => {
      const labels = el.getAttribute("data-fmt").split(" ")
        .map((k) => FORMAT_LABELS[k] || k)
        .join(", ");
      if (!labels) return;

      const title = `서식 변경: ${labels}`;
      el.setAttribute("title", title);

      // 행(tr)에 준 title은 셀 위에서 잡히지 않는다. 정작 눈에 보이는 것은 셀이므로
      // (행 높이 표시 막대도 첫 셀에 그린다) 자기 표시가 없는 셀에 같은 설명을 복사해
      // 행 어디에 올려도 뜨게 한다.
      if (el.tagName !== "TR") return;
      Array.from(el.children).forEach((cell) => {
        if (cell.hasAttribute("data-fmt")) return;
        cell.setAttribute("title", title);
        cell.setAttribute("data-fmt-row", ""); // 커서 표시용 (설명은 행에서 받아온 것)
      });
    });
  }, [narrative]);

  if (!narrative?.changed) return <Note>변경 없음</Note>;

  const fmtLabels = (narrative.fmt || []).map((k) => FORMAT_LABELS[k] || k);

  return (
    <>
      <Legend>
        <Ins>추가</Ins>
        <Del>삭제</Del>
        <Fmt>서식 변경</Fmt>
        <span>
          왼쪽에 색 막대가 붙은 문단·행·셀은 덩어리 자체가 바뀐 것입니다.
          보라색 막대는 행 높이·열 너비처럼 글자에 자국이 남지 않는 서식 변경으로,
          마우스를 올리면 무엇이 바뀌었는지 보여 줍니다.
        </span>
      </Legend>
      {!!fmtLabels.length && <Note>바뀐 서식: {fmtLabels.join(", ")}</Note>}
      <NarrativeBox
        ref={boxRef}
        dangerouslySetInnerHTML={{ __html: withTableWrappers(narrative.html) }}
      />
    </>
  );
}

function SectionDiff({ sections }) {
  const { added = [], removed = [], renamed = [] } = sections || {};
  if (!added.length && !removed.length && !renamed.length) return null;
  return (
    <>
      <SubLabel>Sections</SubLabel>
      {added.map((s) => (
        <Item key={`a${s.sectionNo}`} $kind="added">
          섹션 {s.sectionNo} 추가 — {s.sectionTitle || "(제목 없음)"}
        </Item>
      ))}
      {removed.map((s) => (
        <Item key={`r${s.sectionNo}`} $kind="removed">
          섹션 {s.sectionNo} 삭제 — {s.sectionTitle || "(제목 없음)"}
        </Item>
      ))}
      {renamed.map((s) => (
        <Item key={`n${s.sectionNo}`} $kind="modified">
          섹션 {s.sectionNo} 제목 변경{" "}
          <OldVal>{s.old || "(제목 없음)"}</OldVal> → <NewVal>{s.new || "(제목 없음)"}</NewVal>
        </Item>
      ))}
    </>
  );
}

export function DiffView({ diff }) {
  if (!diff) return <Empty>변경 내역을 불러올 수 없습니다.</Empty>;

  if (diff.isFirstVersion && !diff.toIsWorking) {
    return <Empty>첫 번째 버전입니다. 비교할 이전 버전이 없습니다.</Empty>;
  }
  if (diff.isEmpty) {
    return <Empty>이전 버전과 동일한 내용입니다.</Empty>;
  }

  const q = diff.questions || {};
  // 선택지의 조건부 표시 대상을 이름으로 풀어 주는 함수. 변경된 질문만 목록에 오르므로
  // 바뀌지 않은 질문까지 담긴 questionIndex(API가 양쪽 버전을 합쳐 보내 준다)로 찾는다.
  const resolveQuestion = makeResolveQuestion(diff.questionIndex);
  const counts = [
    ["added",    "추가",     q.added?.length],
    ["removed",  "삭제",     q.removed?.length],
    ["modified", "수정",     q.modified?.length],
    ["moved",    "순서 변경", q.moved?.length],
    ["renamed",  "key 변경",  q.renamed?.length],
  ].filter(([, , n]) => n > 0);

  // Narrative counts are per block — a paragraph, list item, table row or cell.
  const nc = diff.narrative?.counts || {};
  const narrativeCounts = [
    ["added",    "Overview 추가", nc.added],
    ["removed",  "Overview 삭제", nc.removed],
    ["modified", "Overview 수정", nc.modified],
    ["renamed",  "Overview 서식", nc.formatted],
  ].filter(([, , n]) => n > 0);

  return (
    <>
      <Summary>
        {counts.length
          ? counts.map(([kind, label, n]) => (
              <Chip key={kind} $kind={kind}>{label} {n}</Chip>
            ))
          : <Chip>질문 변경 없음</Chip>}
        {narrativeCounts.map(([kind, label, n]) => (
          <Chip key={label} $kind={kind}>{label} {n}</Chip>
        ))}
      </Summary>

      <SubLabel>Study Overview</SubLabel>
      <NarrativeDiff narrative={diff.narrative} />

      <SectionDiff sections={diff.sections} />

      {!!counts.length && <SubLabel>Questions</SubLabel>}

      {!!q.added?.length && (
        <Group>
          <GroupTitle $kind="added">추가된 질문 {q.added.length}</GroupTitle>
          {q.added.map((item) => (
            <Item key={item.questionKey} $kind="added"><QuestionLabel q={item} byKey={diff.questionIndex} /></Item>
          ))}
        </Group>
      )}

      {!!q.removed?.length && (
        <Group>
          <GroupTitle $kind="removed">삭제된 질문 {q.removed.length}</GroupTitle>
          <Note>
            삭제된 질문에 이미 받은 응답은 남아 있지만, 이후 발송분에는 표시되지 않습니다.
          </Note>
          {q.removed.map((item) => (
            <Item key={item.questionKey} $kind="removed"><QuestionLabel q={item} byKey={diff.questionIndex} /></Item>
          ))}
        </Group>
      )}

      {!!q.modified?.length && (
        <Group>
          <GroupTitle $kind="modified">수정된 질문 {q.modified.length}</GroupTitle>
          {q.modified.map((item) => (
            <Item key={item.questionKey} $kind="modified">
              <QuestionLabel q={item} byKey={diff.questionIndex} />
              {Object.entries(item.fields).map(([field, v]) => (
                field === "options" ? (
                  <OptionsDiff key={field} value={v} resolve={resolveQuestion} />
                ) : (
                  <FieldRow key={field}>
                    {FIELD_LABELS[field] || field}:{" "}
                    <OldVal>{fmtValue(field, v.old, resolveQuestion)}</OldVal>
                    {" → "}
                    <NewVal>{fmtValue(field, v.new, resolveQuestion)}</NewVal>
                  </FieldRow>
                )
              ))}
            </Item>
          ))}
        </Group>
      )}

      {!!q.moved?.length && (
        <Group>
          <GroupTitle $kind="moved">순서가 바뀐 질문 {q.moved.length}</GroupTitle>
          {q.moved.map((item) => (
            <Item key={item.questionKey} $kind="moved">
              <QuestionLabel q={item} byKey={diff.questionIndex} />
              <Meta>
                이전 위치: 섹션 {item.oldSectionNo} / {item.oldQuestionNo}번 → 섹션{" "}
                {item.sectionNo} / {item.questionNo}번
              </Meta>
            </Item>
          ))}
        </Group>
      )}

      {!!q.renamed?.length && (
        <Group>
          <GroupTitle $kind="renamed">question key가 바뀐 질문 {q.renamed.length}</GroupTitle>
          <Note>
            내용은 그대로지만 key가 바뀌었습니다. 이전 key로 받은 응답은 이어지지 않습니다.
          </Note>
          {q.renamed.map((item) => (
            <Item key={item.new.questionKey} $kind="renamed">
              <QuestionLabel q={item.new} byKey={diff.questionIndex} />
              <FieldRow>
                key: <OldVal>{item.old.questionKey}</OldVal> → <NewVal>{item.new.questionKey}</NewVal>
              </FieldRow>
            </Item>
          ))}
        </Group>
      )}

      {q.unchangedCount > 0 && (
        <Note style={{ marginTop: 14 }}>변경되지 않은 질문 {q.unchangedCount}개는 표시하지 않았습니다.</Note>
      )}
    </>
  );
}

// `loading` covers the diff fetch; `error` a failed one. Both arrive on a later render.
export function DiffModal({ title, subtitle, diff, loading, error, onClose }) {
  return (
    <Backdrop onClick={onClose}>
      <Modal $wide onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>{title}</Title>
          <CloseBtn onClick={onClose}>×</CloseBtn>
        </Header>
        <Body>
          {loading ? (
            <Loading>Loading...</Loading>
          ) : error ? (
            <Empty>{error}</Empty>
          ) : (
            <>
              {subtitle && (
                <div style={{ fontSize: 12, color: "#6c757d", marginBottom: 12 }}>{subtitle}</div>
              )}
              <DiffView diff={diff} />
            </>
          )}
        </Body>
      </Modal>
    </Backdrop>
  );
}
